import {
  getIsDecimalCommaLocale,
  getLocaleStringWithPrecision,
  toFixed,
} from "@shared/utils/numbers";
import { compactAxisValue } from "features/bots/NewLook/newLookBotUtils.ts";

const FIXED_DECIMALS_MAX_PRECISION = 2;
const fixedDecimalsFormatters = new Map<string, Intl.NumberFormat>();

const getFixedDecimalsFormatter = (precision: number) => {
  const locale = navigator.language || undefined;
  const key = `${locale}_${precision}`;
  if (!fixedDecimalsFormatters.has(key))
    fixedDecimalsFormatters.set(
      key,
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      })
    );
  return fixedDecimalsFormatters.get(key)!;
};

export const formatAmount = (value: number, precision: number) =>
  precision > 0 && precision <= FIXED_DECIMALS_MAX_PRECISION
    ? getFixedDecimalsFormatter(precision).format(toFixed(value, precision))
    : getLocaleStringWithPrecision(value, precision);

/**
 * The value as its figure reads at `precision` (cut like
 * getLocaleStringWithPrecision), so a figure shown as 0 takes no sign.
 */
export const shownAt = (value: number, precision: number) =>
  toFixed(value, precision) || 0;

export const formatMoney = (value: number, symbol: string, precision: number) =>
  `${shownAt(value, precision) < 0 ? "−" : ""}${symbol}${formatAmount(
    Math.abs(value),
    precision
  )}`;

export const formatSignedMoney = (
  value: number,
  symbol: string,
  precision: number
) =>
  `${shownAt(value, precision) > 0 ? "+" : ""}${formatMoney(
    value,
    symbol,
    precision
  )}`;

export const formatSignedPercent = (value: number, digits = 2) => {
  const shown = Number(value.toFixed(digits));
  return `${shown > 0 ? "+" : shown < 0 ? "−" : ""}${Math.abs(value).toFixed(
    digits
  )}%`;
};

/**
 * A chart guide's value in compact form ("$12.3K", "₿0.0123"), with as many
 * decimals as the step between the guides needs to tell them apart.
 */
export const formatCompactMoney = (
  value: number,
  symbol: string,
  step: number
) => compactAxisValue(value, step, { prefix: symbol });

/**
 * A formatted amount's whole part and its fraction, the fraction with the
 * locale's decimal separator ("12,345.67" -> ["12,345", ".67"]; "12.345,67"
 * -> ["12.345", ",67"]), for headlines that set the fraction apart.
 */
export const splitFraction = (text: string): [string, string] => {
  const index = text.lastIndexOf(getIsDecimalCommaLocale() ? "," : ".");
  return index === -1 ? [text, ""] : [text.slice(0, index), text.slice(index)];
};
